import React, { useEffect } from "react";
import { Row, Col, Input, Button, Card, CardBody, CardHeader, FormGroup, Label } from "reactstrap";
import { useFormik } from "formik";
import { bankAccountSchema } from "../../../../models/bankAccount";
import { BANK_OPTIONS, ACCOUNT_TYPES } from "../constants";
import { OverlayLoader, LoadingButton } from "../../../../components/Common";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";
import PropTypes from "prop-types";

const BankAccountEditor = ({ account, onSave, onDelete, isSaving, onChange }) => {
    const formik = useFormik({
        initialValues: account || {},
        validationSchema: bankAccountSchema,
        enableReinitialize: true,
        onSubmit: (values) => onSave(values)
    });

    // useEffect que resetava o formulário removido pois enableReinitialize já trata isso.

    useEffect(() => {
        if (onChange) {
            onChange(formik.dirty);
        }
    }, [formik.dirty, onChange]);

    if (!account) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-0 display-4">
                            <i className="mdi mdi-bank-plus"></i>
                        </div>
                    </div>
                    <h5>Selecione uma conta</h5>
                    <p className="text-muted text-truncate" style={{ maxWidth: '300px' }}>
                        Escolha uma conta bancária ao lado ou crie uma nova para gerenciar seu financeiro.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <Card className="border-0 shadow-sm overflow-hidden rounded-0 position-relative h-100">
            <form onSubmit={formik.handleSubmit} className="h-100 d-flex flex-column">
                <CardHeader className="bg-white py-3 border-bottom">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <div className="avatar-sm">
                                <span className="avatar-title bg-primary bg-gradient text-white rounded-0 shadow-sm">
                                    <i className="mdi mdi-bank-transfer font-size-22"></i>
                                </span>
                            </div>
                            <div className="ms-3">
                                <h5 className="mb-0 fw-bold text-dark">
                                    {account.id ? "Editar Conta Bancária" : "Nova Conta Bancária"}
                                </h5>
                                <p className="text-muted mb-0 small">Dados básicos e saldo inicial</p>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            {account.id && (
                                <Button
                                    type="button"
                                    color="light"
                                    className="btn-md rounded-0 px-3 shadow-none text-danger border-0"
                                    onClick={() => onDelete(account.id, account.description)}
                                    disabled={isSaving}
                                >
                                    <i className="mdi mdi-trash-can-outline font-size-16 me-1"></i> Excluir
                                </Button>
                            )}
                            <LoadingButton
                                color="primary"
                                type="submit"
                                className="btn-md rounded-0 px-4 shadow-sm"
                                loading={isSaving}
                                loadingText="Salvando..."
                            >
                                Salvar
                            </LoadingButton>
                        </div>
                    </div>
                </CardHeader>

                <CardBody className="p-4 overflow-auto">
                    <OverlayLoader show={isSaving} label="Salvando..." />

                    <Row className="mb-4">
                        <Col lg={12}>
                            <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                                <i className="mdi mdi-information-outline me-2 font-size-16 text-primary"></i>
                                Configurações da Conta
                            </h6>
                        </Col>
                        <Col md={8}>
                            <FormGroup className="mb-3">
                                <Label className="fw-medium text-muted small text-uppercase">Nome da Conta / Identificação <span className="text-danger">*</span></Label>
                                <Input
                                    name="description"
                                    placeholder="Ex: Conta Principal PJ, Caixa Interno..."
                                    value={formik.values.description || ''}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={formik.touched.description && !!formik.errors.description}
                                    className="form-control bg-light-subtle border-0"
                                />
                                {formik.touched.description && formik.errors.description && <div className="invalid-feedback">{formik.errors.description}</div>}
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup className="mb-3">
                                <Label className="fw-medium text-muted small text-uppercase">Status</Label>
                                <Input
                                    type="select"
                                    name="status"
                                    value={formik.values.status || GLOBAL_STATUS.ACTIVE}
                                    onChange={formik.handleChange}
                                    className="form-select bg-light-subtle border-0"
                                >
                                    <option value={GLOBAL_STATUS.ACTIVE}>{GLOBAL_STATUS_LABELS[GLOBAL_STATUS.ACTIVE]} (Em uso)</option>
                                    <option value={GLOBAL_STATUS.INACTIVE}>{GLOBAL_STATUS_LABELS[GLOBAL_STATUS.INACTIVE]} (Ignorar)</option>
                                </Input>
                            </FormGroup>
                        </Col>

                        <Col md={6}>
                            <FormGroup className="mb-3">
                                <Label className="fw-medium text-muted small text-uppercase">Banco</Label>
                                <Input
                                    type="select"
                                    name="bankName"
                                    value={formik.values.bankName || ''}
                                    onChange={formik.handleChange}
                                    className="form-select bg-light-subtle border-0"
                                >
                                    <option value="">Selecione um banco...</option>
                                    {BANK_OPTIONS.map(bank => (
                                        <option key={bank} value={bank}>{bank}</option>
                                    ))}
                                </Input>
                                {formik.touched.bankName && formik.errors.bankName && <div className="text-danger small">{formik.errors.bankName}</div>}
                            </FormGroup>
                        </Col>

                        <Col md={6}>
                            <FormGroup className="mb-3">
                                <Label className="fw-medium text-muted small text-uppercase">Tipo de Conta</Label>
                                <Input
                                    type="select"
                                    name="accountType"
                                    value={formik.values.accountType || ''}
                                    onChange={formik.handleChange}
                                    className="form-select bg-light-subtle border-0"
                                >
                                    {ACCOUNT_TYPES.map(type => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>

                        <Col md={4}>
                            <FormGroup className="mb-3">
                                <Label className="fw-medium text-muted small text-uppercase">Agência</Label>
                                <Input
                                    name="agency"
                                    placeholder="0001"
                                    value={formik.values.agency || ''}
                                    onChange={formik.handleChange}
                                    className="form-control bg-light-subtle border-0 text-center font-monospace"
                                />
                            </FormGroup>
                        </Col>

                        <Col md={4}>
                            <FormGroup className="mb-3">
                                <Label className="fw-medium text-muted small text-uppercase">Nº Conta</Label>
                                <Input
                                    name="accountNumber"
                                    placeholder="12345-6"
                                    value={formik.values.accountNumber || ''}
                                    onChange={formik.handleChange}
                                    className="form-control bg-light-subtle border-0 text-center font-monospace"
                                />
                            </FormGroup>
                        </Col>

                        <Col md={4}>
                            <FormGroup className="mb-3">
                                <Label className="fw-medium text-muted small text-uppercase">Saldo Inicial (R$) <span className="text-danger">*</span></Label>
                                <Input
                                    name="initialBalance"
                                    type="number"
                                    step="0.01"
                                    placeholder="0,00"
                                    value={formik.values.initialBalance !== undefined ? formik.values.initialBalance : ''}
                                    onChange={formik.handleChange}
                                    disabled={!!account.id}
                                    className="form-control bg-light-subtle border-0 fw-bold text-primary text-end"
                                />
                                <small className="text-muted">Saldo disponível no momento do cadastro.</small>
                            </FormGroup>
                        </Col>
                    </Row>
                </CardBody>
            </form>
        </Card>
    );
};

BankAccountEditor.propTypes = {
    account: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default BankAccountEditor;
